/**
 * Integration create/edit modal for DP Operator Console.
 * Per INTEGRATIONS_ARCHITECTURE.md §6.2.
 */
import { useState } from 'react';

const PROVIDERS = ['anthropic', 'openai', 'google', 'mistral'];
const TYPES = ['llm_chat', 'transcription', 'vision', 'embeddings', 'email_transactional', 'web_search'];

export default function IntegrationFormModal({ integration, onSave, onClose, tenantId }) {
  const isEdit = !!integration;

  const [provider, setProvider] = useState(integration?.provider || 'anthropic');
  const [integrationType, setIntegrationType] = useState(integration?.integration_type || 'llm_chat');
  const [apiKey, setApiKey] = useState('');
  const [config, setConfig] = useState(JSON.stringify(integration?.config || {}, null, 2));
  const [enabled, setEnabled] = useState(integration?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      let configParsed = {};
      if (config.trim()) {
        try { configParsed = JSON.parse(config); }
        catch { setError('Invalid JSON in config field'); setSaving(false); return; }
      }

      const payload = { enabled };
      if (!isEdit) {
        payload.tenant_id = tenantId;
        payload.provider = provider;
        payload.integration_type = integrationType;
      }
      if (apiKey.trim()) {
        payload.credentials = { api_key: apiKey.trim() };
      } else if (!isEdit) {
        setError('API key is required for new integrations');
        setSaving(false);
        return;
      }
      if (config.trim()) {
        payload.config = configParsed;
      }

      await onSave(payload);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-[var(--aif-bg-surface)] rounded-xl shadow-xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Integration' : 'Add Integration'}</h2>
          <button onClick={onClose} className="text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]">X</button>
        </div>

        {error && <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 p-2 rounded text-sm mb-3">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isEdit && (
            <>
              <div>
                <label className="block text-xs font-medium text-[var(--aif-text-muted)] mb-1">Provider</label>
                <select value={provider} onChange={e => setProvider(e.target.value)}
                  className="w-full border border-[var(--aif-border-default)] rounded px-3 py-2 text-sm bg-[var(--aif-bg-page)]">
                  {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--aif-text-muted)] mb-1">Integration Type</label>
                <select value={integrationType} onChange={e => setIntegrationType(e.target.value)}
                  className="w-full border border-[var(--aif-border-default)] rounded px-3 py-2 text-sm bg-[var(--aif-bg-page)]">
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-[var(--aif-text-muted)] mb-1">
              API Key {isEdit && '(leave empty to keep current)'}
            </label>
            <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
              placeholder={isEdit ? '********' : 'sk-ant-...'}
              className="w-full border border-[var(--aif-border-default)] rounded px-3 py-2 text-sm bg-[var(--aif-bg-page)]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--aif-text-muted)] mb-1">Config (JSON, optional)</label>
            <textarea value={config} onChange={e => setConfig(e.target.value)} rows={3}
              className="w-full border border-[var(--aif-border-default)] rounded px-3 py-2 text-xs font-mono bg-[var(--aif-bg-page)]"
            />
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} id="enabled-check" />
            <label htmlFor="enabled-check" className="text-sm">Enabled</label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded border border-[var(--aif-border-default)]">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm rounded bg-[var(--aif-color-primary)] text-white disabled:opacity-50">
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
